/**
 * Whole-row changes to a table in a stored document, written as Word writes
 * them: a new row is a clone of the row it follows, marked as one tracked
 * insertion on the row and on every paragraph in it; a removed row is marked
 * as one tracked deletion the same two ways; a new table is rows built from
 * text alone on a plain grid.
 *
 * The shape is read off Word's own output. An inserted row carries
 * `w:trPr/w:ins`, every paragraph mark `w:pPr/w:rPr/w:ins`, every run inside
 * `w:ins`. A deleted row carries `w:trPr/w:del`, every paragraph mark
 * `w:pPr/w:rPr/w:del`, every run inside `w:del` with `w:delText`. Word shows
 * either as one change per row and Accept and Reject act on the row.
 *
 * The clone keeps the template row's structure and cell formatting and drops
 * its identity and revision state: a header flag, banding, another author's
 * insertion or deletion, and Word's `rsid` attributes never travel to the new
 * row. Cell content is never cloned; a new cell holds only the text asked for,
 * in the template cell's first paragraph's properties, so numbering continues
 * and fields or content controls in the template are never duplicated.
 *
 * The cloner never names a paragraph. New cell paragraphs carry no paragraph
 * bookmark; the reader's naming pass names them on the next read.
 */
import { WORDML_2010_NS, WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "./ooxml-names";
import { ParagraphRevisionMarks } from "./paragraph-revision-marks";
import { ParagraphXml } from "./paragraph-xml";
import type { Revision } from "./revision";
import { TableStructure, type TableRowStructure } from "./table-structure";
import type { TablePlacement } from "../wire/table-placement";
import { XmlHost } from "../xml-host";

/**
 * What a new row does under a vertically merged cell of the template row.
 *
 * CASES
 *   - "join"      -> the new row's cell continues the merge above it and holds no text
 *   - "start_new" -> the new row's cell opens its own merged group and takes text
 */
export type MergedCellsBehaviour = "join" | "start_new";

export interface InsertTableRowsAfterRequest {
    structure: TableStructure;
    /** The row whose structure and cell formatting the new rows copy. */
    templateRow: TableRowStructure;
    /** The row the first new row goes after; the template row itself, or the last row an earlier operation placed after it. */
    afterRowElement: Element;
    /** One entry per new row, each with one text per cell of the template row; "\n" in a text starts a new paragraph in the cell. */
    rows: string[][];
    mergedCells: MergedCellsBehaviour;
    revision: Revision;
}

export interface InsertedTableRows {
    rowElements: Element[];
    /** Every paragraph written into the new rows, in document order; each is unnamed. */
    paragraphElements: Element[];
}

export interface DeleteTableRowRequest {
    row: TableRowStructure;
    revision: Revision;
}

export interface InsertTableAfterRequest {
    /** The paragraph the placement is measured from. */
    afterParagraph: Element;
    /** Where the table goes relative to that paragraph (`TablePlacement`): right after it, nesting inside its cell, or after the outermost table holding it. */
    place: TablePlacement;
    /** The first row is the header row; every row has the same number of cells. */
    rows: string[][];
    revision: Revision;
}

export interface InsertedTable {
    tableElement: Element;
    paragraphElements: Element[];
}

/** Row properties that describe this row's own state, never the row cloned from it. */
const ROW_PROPERTIES_NOT_CLONED = new Set(["tblHeader", "cnfStyle", "hidden", "ins", "del", "trPrChange"]);
/** Cell properties that record a revision of this cell, never of a cell cloned from it. */
const CELL_PROPERTIES_NOT_CLONED = new Set(["cellIns", "cellDel", "cellMerge", "tcPrChange"]);
/** Run properties of a paragraph mark that are revision records, not formatting, so a new run must not inherit them. */
const RUN_PROPERTIES_NOT_INHERITED = new Set(["ins", "del", "moveFrom", "moveTo", "rPrChange"]);
/** The width of a new table in twentieths of a point: 6.5 inches, the text width of a Letter page with one-inch margins. */
const NEW_TABLE_WIDTH_DXA = 9360;
/** The `w:tblLook` mask Word writes for a plain grid whose first row is a header. */
const NEW_TABLE_LOOK_MASK = "04A0";

export class TableRowXml {
    /**
     * Clones the template row once per entry of `rows`, fills each clone's
     * cells with the texts, marks every clone as an insertion by `revision`,
     * and places the clones one after another starting after
     * `afterRowElement`.
     *
     * EXAMPLE
     *   template row  = "Premium support | percentage of platform fee | 18%"
     *   rows          = [["Annual uplift cap", "percentage of prior-year fee", "3%"]]
     *   -> one new w:tr after the template, three cells, each cell one paragraph in w:ins
     */
    static insertRowsAfter(request: InsertTableRowsAfterRequest): InsertedTableRows {
        const rowElements: Element[] = [];
        const paragraphElements: Element[] = [];
        let placeAfter = request.afterRowElement;
        for (const cellTexts of request.rows) {
            /**
             * WHEN a row carries fewer or more texts than the template has cells
             *   CONSEQUENCE
             *     Refuse: the operation layer checks this first, so reaching here is a caller defect, not a model mistake.
             */
            if (cellTexts.length !== request.templateRow.cellElements.length) {
                throw new Error(`A new row needs ${request.templateRow.cellElements.length} cell texts, ${cellTexts.length} given.`);
            }
            const clonedRow = TableRowXml.cloneRowShell(request.templateRow.rowElement, request.revision);
            request.templateRow.cellElements.forEach((templateCellElement, cellIndex) => {
                const templateCellRecord = request.structure.cellRecordOf(templateCellElement);
                const templateCellIsMerged = templateCellRecord !== null && templateCellRecord.verticalMerge !== "none";
                /**
                 * CASES, for the text a new cell takes
                 *   - "join" under a merged template cell -> none: the cell continues the merge and Word shows the cell above
                 *   - otherwise                           -> the text asked for
                 */
                const cellText = request.mergedCells === "join" && templateCellIsMerged ? "" : cellTexts[cellIndex];
                const clonedCell = TableRowXml.cloneCell(templateCellElement, cellText, request.mergedCells, request.revision);
                clonedRow.appendChild(clonedCell.cellElement);
                paragraphElements.push(...clonedCell.paragraphElements);
            });
            placeAfter.parentNode?.insertBefore(clonedRow, placeAfter.nextSibling);
            rowElements.push(clonedRow);
            placeAfter = clonedRow;
        }
        return { rowElements, paragraphElements };
    }

    /**
     * Marks `row` as deleted by `revision`: `w:trPr/w:del` on the row and the
     * deletion shape on every paragraph of every cell.
     */
    static deleteRow(request: DeleteTableRowRequest): void {
        const rowElement = request.row.rowElement;
        const ownerDocument = rowElement.ownerDocument as Document;
        const rowProperties = TableRowXml.rowPropertiesOf(rowElement);
        /**
         * WHEN the row is already a tracked deletion
         *   CONSEQUENCE
         *     Leave the row record alone; a second w:del on one row is markup Word rejects.
         */
        if (TableStructure.childNamed(rowProperties, "del") === null) rowProperties.appendChild(request.revision.stamp(ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:del")));
        for (const cellElement of request.row.cellElements) {
            for (const paragraph of TableRowXml.paragraphsOfCell(cellElement)) ParagraphRevisionMarks.markDeleted(paragraph, request.revision);
        }
    }

    /**
     * Builds a table from `rows` on a plain single-line grid with equal column
     * widths, the first row marked as a header row, every row and paragraph
     * marked as an insertion by `revision`, and places it after
     * `afterParagraph`. When the table would end the body or touch another
     * table, an inserted empty paragraph follows it, as Word requires.
     *
     * CASES, for `place` from a cell paragraph
     *   - "after_paragraph" -> right after the paragraph, inside its cell: a nested table, followed by the empty
     *                          paragraph a cell must end with
     *   - "after_table"     -> after the outermost table holding the cell, behind an inserted empty paragraph
     *                          that keeps the two tables apart
     * From a body paragraph both placements put the table right after it.
     *
     * EXAMPLE
     *   rows = [["Component", "Unit"], ["Platform", "per year"]]
     *   -> w:tbl with 2 gridCol of 4680 dxa, row 1 tblHeader, 4 cells, 4 paragraphs
     */
    static insertTableAfter(request: InsertTableAfterRequest): InsertedTable {
        const ownerDocument = request.afterParagraph.ownerDocument as Document;
        const columnCount = request.rows[0]?.length ?? 0;
        /**
         * WHEN the rows are empty or ragged
         *   CONSEQUENCE
         *     Refuse: the operation layer checks this first, so reaching here is a caller defect.
         */
        if (columnCount === 0 || request.rows.some((row) => row.length !== columnCount)) throw new Error("A new table needs at least one row and the same number of cells in every row.");
        const tableElement = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:tbl");
        tableElement.appendChild(TableRowXml.parseFragment(ownerDocument, TableRowXml.newTablePropertiesXml()));
        /**
         * EXAMPLE
         *   columnCount = 3 -> each gridCol and tcW is floor(9360 / 3) = 3120 dxa
         */
        const columnWidthDxa = Math.floor(NEW_TABLE_WIDTH_DXA / columnCount);
        tableElement.appendChild(TableRowXml.parseFragment(ownerDocument, `<w:tblGrid xmlns:w="${WORDPROCESSINGML_NS}">${`<w:gridCol w:w="${columnWidthDxa}"/>`.repeat(columnCount)}</w:tblGrid>`));
        const paragraphElements: Element[] = [];
        request.rows.forEach((cellTexts, rowIndex) => {
            const rowElement = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:tr");
            const rowProperties = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:trPr");
            /**
             * WHEN this is the first row
             *   CONSEQUENCE
             *     It is the header row: Word repeats it on every page and styles it through tblLook.
             */
            if (rowIndex === 0) rowProperties.appendChild(ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:tblHeader"));
            rowProperties.appendChild(request.revision.stamp(ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:ins")));
            rowElement.appendChild(rowProperties);
            for (const cellText of cellTexts) {
                const cellElement = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:tc");
                cellElement.appendChild(TableRowXml.parseFragment(ownerDocument, `<w:tcPr xmlns:w="${WORDPROCESSINGML_NS}"><w:tcW w:w="${columnWidthDxa}" w:type="dxa"/></w:tcPr>`));
                for (const paragraph of TableRowXml.insertedParagraphsFromText(ownerDocument, cellText, null, request.revision)) {
                    cellElement.appendChild(paragraph);
                    paragraphElements.push(paragraph);
                }
                rowElement.appendChild(cellElement);
            }
            tableElement.appendChild(rowElement);
        });
        const enclosingTable = request.place === "after_table" ? TableRowXml.outermostTableAround(request.afterParagraph) : null;
        /**
         * CASES, for what the new table is placed after
         *   - "after_paragraph", or a body paragraph  -> that paragraph
         *   - "after_table" from a cell               -> an inserted empty paragraph placed after the outermost table holding it
         */
        let placeAfter: Element = request.afterParagraph;
        if (enclosingTable !== null) {
            const [separatorParagraph] = TableRowXml.insertedParagraphsFromText(ownerDocument, "", null, request.revision);
            enclosingTable.parentNode?.insertBefore(separatorParagraph, enclosingTable.nextSibling);
            paragraphElements.unshift(separatorParagraph);
            placeAfter = separatorParagraph;
        }
        placeAfter.parentNode?.insertBefore(tableElement, placeAfter.nextSibling);
        const followingSibling = TableRowXml.nextElementSibling(tableElement);
        const tableIsFollowedByParagraph = followingSibling !== null && elementNameWithoutPrefix(followingSibling) === "p";
        /**
         * WHEN nothing, a section break or another table follows the new table
         *   CONSEQUENCE
         *     Add an inserted empty paragraph after it: Word requires a paragraph between two tables and before the end of the body.
         */
        if (!tableIsFollowedByParagraph) {
            const [trailingParagraph] = TableRowXml.insertedParagraphsFromText(ownerDocument, "", null, request.revision);
            tableElement.parentNode?.insertBefore(trailingParagraph, tableElement.nextSibling);
            paragraphElements.push(trailingParagraph);
        }
        return { tableElement, paragraphElements };
    }

    /**
     * A new `w:tr` with the template's `w:tblPrEx` and `w:trPr` minus the
     * children that describe the template's own state, a stamped `w:ins`
     * appended to the row properties, and no `rsid` or paragraph identity
     * attributes. Cells are added by the caller.
     */
    private static cloneRowShell(templateRowElement: Element, revision: Revision): Element {
        const ownerDocument = templateRowElement.ownerDocument as Document;
        const clonedRow = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:tr");
        const tablePropertyExceptions = TableStructure.childNamed(templateRowElement, "tblPrEx");
        if (tablePropertyExceptions !== null) clonedRow.appendChild(tablePropertyExceptions.cloneNode(true));
        const templateRowProperties = TableStructure.childNamed(templateRowElement, "trPr");
        const clonedRowProperties = templateRowProperties === null ? ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:trPr") : (templateRowProperties.cloneNode(true) as Element);
        TableRowXml.removeChildrenNamed(clonedRowProperties, ROW_PROPERTIES_NOT_CLONED);
        clonedRowProperties.appendChild(revision.stamp(ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:ins")));
        clonedRow.appendChild(clonedRowProperties);
        return clonedRow;
    }

    /**
     * A new `w:tc` with the template cell's formatting and the text asked
     * for, every paragraph marked as an insertion.
     *
     * CASES, for `w:vMerge` on the clone
     *   - "join",      template "restart" or "continue" -> "continue": the new cell joins the merged group above
     *   - "start_new", template "restart" or "continue" -> "restart": the new cell opens its own group
     *   - template "none"                                -> no vMerge
     */
    private static cloneCell(templateCellElement: Element, cellText: string, mergedCells: MergedCellsBehaviour, revision: Revision): { cellElement: Element; paragraphElements: Element[] } {
        const ownerDocument = templateCellElement.ownerDocument as Document;
        const clonedCell = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:tc");
        const templateCellProperties = TableStructure.childNamed(templateCellElement, "tcPr");
        if (templateCellProperties !== null) {
            const clonedCellProperties = templateCellProperties.cloneNode(true) as Element;
            TableRowXml.removeChildrenNamed(clonedCellProperties, CELL_PROPERTIES_NOT_CLONED);
            const verticalMerge = TableStructure.childNamed(clonedCellProperties, "vMerge");
            if (verticalMerge !== null) {
                if (mergedCells === "join") {
                    verticalMerge.removeAttributeNS(WORDPROCESSINGML_NS, "val");
                    verticalMerge.removeAttribute("w:val");
                } else verticalMerge.setAttributeNS(WORDPROCESSINGML_NS, "w:val", "restart");
            }
            clonedCell.appendChild(clonedCellProperties);
        }
        const templateFirstParagraph = TableRowXml.paragraphsOfCell(templateCellElement)[0] ?? null;
        const paragraphElements = TableRowXml.insertedParagraphsFromText(ownerDocument, cellText, templateFirstParagraph, revision);
        for (const paragraph of paragraphElements) clonedCell.appendChild(paragraph);
        return { cellElement: clonedCell, paragraphElements };
    }

    /**
     * One inserted paragraph per line of `text` (at least one), each with the
     * template paragraph's inheritable properties, a run in the template
     * mark's formatting for a non-blank line, and no run for a blank one.
     *
     * EXAMPLE
     *   text = "Sev 1\nCritical" -> two paragraphs; text = "" -> one empty paragraph
     *   text = "Sev 1\r\nCritical" -> the same two paragraphs, neither ending in a w:br
     */
    private static insertedParagraphsFromText(ownerDocument: Document, text: string, templateParagraph: Element | null, revision: Revision): Element[] {
        const templateParagraphXml = templateParagraph === null ? "" : XmlHost.xmlSerializer().serializeToString(templateParagraph);
        const inheritableRunProperties = templateParagraph === null ? null : TableRowXml.inheritableRunPropertiesOfMark(templateParagraph);
        /**
         * CASES, for what ends a line of the cell text
         *   - "\r\n"      -> one line ending: the "\r" stays with the "\n" and never reaches a paragraph
         *   - a lone "\r" -> one line ending
         *   - "\n"        -> one line ending
         *
         * WHY "\r\n" is matched before "\r"
         *   Split on "\n" alone, "Sev 1\r\nCritical" left "Sev 1\r" for the paragraph writer, which
         *   wrote the "\r" as a w:br: an empty line under "Sev 1" in the new cell.
         */
        const LINE_ENDING_OF_CELL_TEXT = /\r\n|\r|\n/;
        const lines = text.split(LINE_ENDING_OF_CELL_TEXT);
        return lines.map((line) => {
            const paragraphXml = ParagraphXml.plainFromText({ text: line, templateParagraphXml });
            const paragraph = ownerDocument.importNode(XmlHost.domParser().parseFromString(paragraphXml, "application/xml").documentElement, true) as Element;
            TableRowXml.stripParagraphIdentity(paragraph);
            const run = TableStructure.childNamed(paragraph, "r");
            /**
             * CASES
             *   - the line is blank      -> drop the empty run the text builder made; Word writes none for an empty paragraph
             *   - the line has text      -> give the run the template mark's formatting minus revision records
             */
            if (run !== null && line.length === 0) paragraph.removeChild(run);
            else if (run !== null && inheritableRunProperties !== null) run.insertBefore(ownerDocument.importNode(inheritableRunProperties, true), run.firstChild);
            ParagraphRevisionMarks.markInserted(paragraph, revision);
            return paragraph;
        });
    }

    /**
     * The template paragraph mark's `w:pPr/w:rPr` as a run's `w:rPr`, minus
     * revision records; null when the mark has none or only records. What
     * Word applies to text typed into an empty cell: the cell's own font and
     * size. Read from the template itself, because the paragraph builder
     * drops the mark's run properties from the properties it copies.
     */
    private static inheritableRunPropertiesOfMark(templateParagraph: Element): Element | null {
        const paragraphProperties = TableStructure.childNamed(templateParagraph, "pPr");
        const markRunProperties = paragraphProperties === null ? null : TableStructure.childNamed(paragraphProperties, "rPr");
        if (markRunProperties === null) return null;
        const runProperties = markRunProperties.cloneNode(true) as Element;
        TableRowXml.removeChildrenNamed(runProperties, RUN_PROPERTIES_NOT_INHERITED);
        return runProperties.childNodes.length === 0 ? null : runProperties;
    }

    /**
     * The outermost `w:tbl` holding a paragraph, or null for a body paragraph.
     * Tables inside a text box (under a `w:p`) do not count as enclosing.
     *
     * EXAMPLE
     *   body > tbl#1 > tr > tc > tbl#2 > tr > tc > p -> tbl#1
     *   body > p                                     -> null
     */
    static outermostTableAround(paragraph: Element): Element | null {
        let outermost: Element | null = null;
        for (let ancestor = paragraph.parentNode; ancestor !== null && ancestor.nodeType === 1; ancestor = ancestor.parentNode) {
            const ancestorName = elementNameWithoutPrefix(ancestor as Element);
            if (ancestorName === "tbl") outermost = ancestor as Element;
            if (ancestorName === "p" || ancestorName === "body") break;
        }
        return outermost;
    }

    /** The paragraphs written directly in a cell, nested tables' paragraphs excluded. */
    static paragraphsOfCell(cellElement: Element): Element[] {
        return Array.from(cellElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p")).filter((paragraph) => TableStructure.nearestAncestorNamed(paragraph, "tc") === cellElement);
    }

    /** `w:trPr` of the row, created after `w:tblPrEx` (or first) when missing, where the row's revision record lives. */
    private static rowPropertiesOf(rowElement: Element): Element {
        const existing = TableStructure.childNamed(rowElement, "trPr");
        if (existing !== null) return existing;
        const ownerDocument = rowElement.ownerDocument as Document;
        const rowProperties = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:trPr");
        const tablePropertyExceptions = TableStructure.childNamed(rowElement, "tblPrEx");
        /**
         * CASES, for where w:trPr goes in a row's content model
         *   - w:tblPrEx present -> right after it
         *   - otherwise         -> first child
         */
        rowElement.insertBefore(rowProperties, tablePropertyExceptions === null ? rowElement.firstChild : tablePropertyExceptions.nextSibling);
        return rowProperties;
    }

    /** A plain grid: automatic width, single hairline borders on every edge, first row styled as a header. */
    private static newTablePropertiesXml(): string {
        const border = (edge: string): string => `<w:${edge} w:val="single" w:sz="4" w:space="0" w:color="auto"/>`;
        return `<w:tblPr xmlns:w="${WORDPROCESSINGML_NS}"><w:tblW w:w="0" w:type="auto"/><w:tblBorders>${["top", "left", "bottom", "right", "insideH", "insideV"].map(border).join("")}</w:tblBorders><w:tblLook w:val="${NEW_TABLE_LOOK_MASK}" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/></w:tblPr>`;
    }

    private static parseFragment(ownerDocument: Document, xml: string): Element {
        return ownerDocument.importNode(XmlHost.domParser().parseFromString(xml, "application/xml").documentElement, true) as Element;
    }

    private static removeChildrenNamed(parent: Element, localNames: ReadonlySet<string>): void {
        for (const child of Array.from(parent.childNodes)) {
            if (child.nodeType === 1 && localNames.has(elementNameWithoutPrefix(child as Element))) parent.removeChild(child);
        }
    }

    /** Drops `w14:paraId`, `w14:textId` and every `w:rsid*` attribute: Word mints these per paragraph and two paragraphs must never share them. */
    private static stripParagraphIdentity(paragraph: Element): void {
        paragraph.removeAttributeNS(WORDML_2010_NS, "paraId");
        paragraph.removeAttributeNS(WORDML_2010_NS, "textId");
        for (const attribute of Array.from(paragraph.attributes)) {
            if (attribute.localName.startsWith("rsid")) paragraph.removeAttributeNode(attribute);
        }
    }

    private static nextElementSibling(element: Element): Element | null {
        for (let sibling = element.nextSibling; sibling !== null; sibling = sibling.nextSibling) {
            if (sibling.nodeType === 1) return sibling as Element;
        }
        return null;
    }
}
